import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, RefreshCw, Loader2 } from "lucide-react";
import { api, API_BASE, tokenStore, orgStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const OPEN_KEY = "sas:sam_open";
const WELCOMED_KEY = "sas:sam_welcomed";
const SAM_AVATAR = "/sam-avatar.png";
const ASK_EVENT = "sas:ask-sam";

/** Imperatively open Sam and auto-send a pre-filled question. */
export function openSamWithPrefill(text) {
  try {
    window.dispatchEvent(new CustomEvent(ASK_EVENT, { detail: { text } }));
  } catch (_) {}
}

const WELCOME_TEXT =
  "Hi, I'm Sam. I'm here to help you get the most out of Symplee. Ask me anything about how to use the tools, what each one does, or where to start. If I don't know the answer, I'll point you to a real human.";

const QUICK_REPLIES = [
  "How do I import leads?",
  "What does each module do?",
  "How do I set up the website chatbot?",
  "Show me how to write an email sequence",
];

const PATH_TO_MODULE = [
  [/^\/app\/chat/, "AI Chat"],
  [/^\/app\/knowledge/, "Knowledge Base"],
  [/^\/app\/leads/, "Smart Leads"],
  [/^\/app\/sequences/, "Outreach Sequences"],
  [/^\/app\/content/, "Content Engine"],
  [/^\/app\/agents/, "AI Agents"],
  [/^\/app\/assistant/, "Executive Assistant"],
  [/^\/app\/integrations/, "Integrations"],
  [/^\/app\/admin/, "Admin Controls"],
  [/^\/app\/settings/, "Workspace Settings"],
  [/^\/app\/?$/, "Workspace Home"],
];

function moduleFromPath(pathname) {
  for (const [rx, name] of PATH_TO_MODULE) {
    if (rx.test(pathname)) return name;
  }
  return "";
}

export function SamHelper() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState("");
  const [showNudge, setShowNudge] = useState(false);
  const [pendingPrefill, setPendingPrefill] = useState(null);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // Restore open state
  useEffect(() => {
    try {
      const stored = localStorage.getItem(OPEN_KEY);
      if (stored === "1") setOpen(true);
    } catch (_) {}
  }, []);

  // Listen for imperative "ask Sam" events from tip cards
  useEffect(() => {
    const handler = (e) => {
      const text = e?.detail?.text;
      setOpen(true);
      setShowNudge(false);
      if (text && typeof text === "string" && text.trim()) {
        setPendingPrefill(text.trim());
      }
    };
    window.addEventListener(ASK_EVENT, handler);
    return () => window.removeEventListener(ASK_EVENT, handler);
  }, []);

  useEffect(() => {
    try { localStorage.setItem(OPEN_KEY, open ? "1" : "0"); } catch (_) {}
  }, [open]);

  // One-time welcome nudge — only AFTER welcome tour is complete
  useEffect(() => {
    if (!user) return;
    if (!user.has_completed_welcome_tour) return;
    try {
      if (localStorage.getItem(WELCOMED_KEY) === "1") return;
    } catch (_) {}
    if (open) {
      // If they opened Sam already, mark as welcomed (no nudge needed)
      try { localStorage.setItem(WELCOMED_KEY, "1"); } catch (_) {}
      return;
    }
    const t1 = setTimeout(() => setShowNudge(true), 1500);
    const t2 = setTimeout(() => {
      setShowNudge(false);
      try { localStorage.setItem(WELCOMED_KEY, "1"); } catch (_) {}
    }, 1500 + 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [user, open]);

  // Load conversation when Sam opens (and on mount once)
  useEffect(() => {
    if (!user) return;
    if (conv) return;
    (async () => {
      try {
        const r = await api.get("/sam/conversations/current");
        setConv({ id: r.data.id, started_at: r.data.started_at });
        setMessages(r.data.messages || []);
      } catch (e) {
        // silent — Sam UI will retry on first send
      }
    })();
  }, [user, conv]);

  // Auto-scroll
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming, open]);

  const currentModule = useMemo(() => moduleFromPath(location.pathname), [location.pathname]);

  // Auto-send pending prefill once the panel is open + conversation is loaded
  useEffect(() => {
    if (!pendingPrefill || !conv || sending) return;
    const text = pendingPrefill;
    setPendingPrefill(null);
    // Fire immediately — the setMessages call inside sendMessage will trigger
    // a re-render; we don't need (and don't want) a setTimeout here because
    // clearing pendingPrefill cancels the cleanup before it can run.
    sendMessage(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrefill, conv, sending]);

  const sendMessage = async (content) => {
    const text = (content || "").trim();
    if (!text || sending || !conv) return;
    setSending(true);
    setStreaming("");

    // Optimistic user message
    const userTemp = {
      id: `tmp_${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, userTemp]);
    setDraft("");

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let acc = "";

    try {
      const orgId = orgStore.get();
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokenStore.get()}`,
      };
      if (orgId) headers["X-Org-Id"] = orgId;
      const resp = await fetch(`${API_BASE}/sam/conversations/${conv.id}/messages`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          content: text,
          current_page_url: location.pathname,
          current_module: currentModule,
        }),
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) {
        const errText = await resp.text().catch(() => "");
        let detail = "Sam couldn't reach the model. Try again in a moment.";
        try { detail = JSON.parse(errText).detail || detail; } catch (_) {}
        throw new Error(detail);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const line = ev.trim();
          if (!line.startsWith("data:")) continue;
          let payload;
          try { payload = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (payload.type === "token") {
            acc += payload.content;
            setStreaming(acc);
          } else if (payload.type === "error") {
            throw new Error(payload.error || "Sam stumbled. Try again.");
          } else if (payload.type === "done") {
            setMessages((m) => [
              ...m,
              { id: payload.assistant_id, role: "assistant", content: acc, created_at: new Date().toISOString() },
            ]);
            setStreaming("");
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error(e.message || "Sam couldn't reply.");
        setMessages((m) => m.filter((x) => x.id !== userTemp.id));
      }
    } finally {
      setSending(false);
      setStreaming("");
      abortRef.current = null;
    }
  };

  const restart = async () => {
    if (!conv) return;
    try {
      const r = await api.delete(`/sam/conversations/${conv.id}`);
      setConv({ id: r.data.new_conversation_id, started_at: new Date().toISOString() });
      setMessages([]);
      setStreaming("");
      toast.success("Sam reset — fresh conversation.");
    } catch (e) {
      toast.error("Couldn't restart — try again.");
    }
  };

  const onKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      sendMessage(draft);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      <AnimatePresence>
        {!open && (
          <motion.button
            type="button"
            onClick={() => { setOpen(true); setShowNudge(false); }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ type: "spring", stiffness: 280, damping: 22 }}
            className="group fixed bottom-5 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full p-[2px] shadow-[0_8px_30px_rgba(79,70,229,0.35)] focus:outline-none"
            style={{
              background: "linear-gradient(135deg, #6366f1 0%, #06b6d4 50%, #14b8a6 100%)",
            }}
            data-testid="sam-bubble"
            aria-label="Ask Sam — your guide to Symplee"
            title="Ask Sam — your guide to Symplee"
          >
            <span
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100"
              style={{
                boxShadow: "0 0 0 6px rgba(99,102,241,0.18)",
                transition: "opacity 200ms",
              }}
            />
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full"
              animate={{ boxShadow: ["0 0 0 0 rgba(99,102,241,0.45)", "0 0 0 14px rgba(99,102,241,0)", "0 0 0 0 rgba(99,102,241,0)"] }}
              transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 5.6, ease: "easeOut" }}
            />
            <img
              src={SAM_AVATAR}
              alt="Sam"
              className="h-full w-full rounded-full bg-white object-cover"
              draggable={false}
            />
          </motion.button>
        )}
      </AnimatePresence>

      {/* First-visit welcome nudge */}
      <AnimatePresence>
        {showNudge && !open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-5 z-[60] max-w-[240px] rounded-2xl border border-indigo-500/30 bg-white px-3 py-2 text-sm text-slate-700 shadow-xl dark:bg-zinc-900 dark:text-zinc-200"
            data-testid="sam-nudge"
          >
            <span className="font-medium text-indigo-600 dark:text-indigo-300">Hi, I'm Sam</span>
            <span className="block text-xs text-slate-500 dark:text-zinc-400">ask me anything.</span>
            <span
              aria-hidden
              className="absolute -bottom-1.5 right-6 h-3 w-3 rotate-45 border-b border-r border-indigo-500/30 bg-white dark:bg-zinc-900"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed bottom-5 right-5 z-[60] flex h-[560px] w-[calc(100vw-1.5rem)] max-w-[380px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl sm:max-h-[calc(100vh-2rem)] dark:border-white/10 dark:bg-[#0a0a0a]"
            style={{ height: "min(560px, calc(100vh - 2rem))" }}
            data-testid="sam-panel"
            role="dialog"
            aria-label="Sam chat panel"
          >
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-slate-200/60 px-4 py-3 dark:border-white/5">
              <div className="h-10 w-10 overflow-hidden rounded-full bg-slate-100 ring-2 ring-indigo-500/30 dark:bg-zinc-800">
                <img src={SAM_AVATAR} alt="Sam" className="h-full w-full object-cover" draggable={false} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-base font-medium text-slate-900 dark:text-white" data-testid="sam-header-name">Sam</div>
                <div className="truncate text-xs text-slate-500 dark:text-white/45">Your guide to Symplee</div>
              </div>
              <button
                type="button"
                onClick={restart}
                className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5 dark:hover:text-white"
                title="Restart conversation"
                aria-label="Restart conversation"
                data-testid="sam-restart"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5 dark:hover:text-white"
                title="Close"
                aria-label="Close Sam"
                data-testid="sam-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 space-y-3 overflow-y-auto bg-gradient-to-b from-slate-50/50 to-white px-3 py-3 dark:from-zinc-900/40 dark:to-[#0a0a0a]"
              data-testid="sam-messages"
            >
              {/* Welcome message — always first */}
              <SamBubble>{WELCOME_TEXT}</SamBubble>

              {/* Quick replies — only when no user messages yet */}
              {messages.filter((m) => m.role === "user").length === 0 && !sending && (
                <div className="flex flex-wrap gap-2" data-testid="sam-quick-replies">
                  {QUICK_REPLIES.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => sendMessage(q)}
                      className="rounded-full border border-indigo-200 bg-indigo-50/60 px-3 py-1.5 text-xs text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-200 dark:hover:bg-indigo-500/20"
                      data-testid={`sam-quick-${q.slice(0, 10).replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Conversation */}
              {messages.map((m) =>
                m.role === "user" ? (
                  <UserBubble key={m.id}>{m.content}</UserBubble>
                ) : (
                  <SamBubble key={m.id}>{m.content}</SamBubble>
                )
              )}

              {/* Streaming bubble */}
              {streaming && <SamBubble streaming>{streaming}</SamBubble>}
              {sending && !streaming && (
                <div className="flex items-center gap-2 pl-9 text-xs text-slate-400 dark:text-white/40" data-testid="sam-typing">
                  <Loader2 className="h-3 w-3 animate-spin" /> Sam is thinking…
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-slate-200/60 bg-white px-3 py-3 dark:border-white/5 dark:bg-[#0a0a0a]">
              <div className="flex items-end gap-2">
                <textarea
                  rows={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Ask Sam anything…"
                  className="max-h-32 flex-1 resize-none rounded-2xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-indigo-400 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/30 dark:focus:border-indigo-400/60"
                  data-testid="sam-input"
                  disabled={sending}
                />
                <button
                  type="button"
                  onClick={() => sendMessage(draft)}
                  disabled={!draft.trim() || sending}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-600 text-white shadow-md transition hover:bg-indigo-700 disabled:opacity-40"
                  data-testid="sam-send"
                  aria-label="Send"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-2 text-[10px] leading-snug text-slate-400 dark:text-white/35" data-testid="sam-escalate">
                Still stuck? Email{" "}
                <a href="mailto:info@symplee.ai" className="underline hover:text-indigo-600 dark:hover:text-indigo-300">
                  info@symplee.ai
                </a>{" "}
                — a real human will help within 24 hours.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function SamBubble({ children, streaming }) {
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5 h-7 w-7 flex-shrink-0 overflow-hidden rounded-full bg-slate-100 ring-1 ring-indigo-500/20 dark:bg-zinc-800">
        <img src={SAM_AVATAR} alt="" className="h-full w-full object-cover" draggable={false} />
      </div>
      <div className="max-w-[78%] rounded-2xl rounded-tl-sm border border-slate-200/70 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm dark:border-white/5 dark:bg-zinc-900/70 dark:text-zinc-100">
        <div className="whitespace-pre-wrap leading-relaxed">{children}</div>
        {streaming && <span className="ml-1 inline-block h-3 w-1 animate-pulse rounded-sm bg-indigo-500/60" />}
      </div>
    </div>
  );
}

function UserBubble({ children }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[78%] rounded-2xl rounded-tr-sm bg-indigo-600 px-3 py-2 text-sm text-white shadow-sm dark:bg-indigo-500">
        <div className="whitespace-pre-wrap leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
